import './Skeleton.css';

export type SkeletonShape = 'rect' | 'circle' | 'pill' | 'line';

type SkeletonProps = {
    width?: string;
    height?: string;
    shape?: SkeletonShape;
    borderRadius?: string;
    className?: string;
};

const SHAPE_RADIUS: Record<SkeletonShape, string> = {
    rect: 'var(--radius-md)',
    circle: '50%',
    pill: 'var(--radius-pill)',
    line: 'var(--radius-sm)',
};

export function Skeleton({
    width = '100%',
    height = '16px',
    shape = 'line',
    borderRadius,
    className = '',
}: SkeletonProps) {
    const radius = borderRadius ?? SHAPE_RADIUS[shape];
    return (
        <div
            className={`skeleton ${className}`}
            style={{ width, height, borderRadius: radius }}
            aria-hidden="true"
        />
    );
}

export function SkeletonCard() {
    return (
        <div className="skeleton-card" role="status" aria-busy="true" aria-label="Loading content">
            <div className="skeleton-card-header">
                <Skeleton width="48px" height="48px" shape="circle" />
                <div className="skeleton-card-info">
                    <Skeleton width="60%" height="14px" />
                    <Skeleton width="40%" height="12px" />
                </div>
            </div>
            <Skeleton width="100%" height="12px" />
            <Skeleton width="80%" height="12px" />
        </div>
    );
}

export function SkeletonMessage({ align = 'left' }: { align?: 'left' | 'right' }) {
    return (
        <div className={`skeleton-message ${align}`}>
            <Skeleton
                width={align === 'right' ? '45%' : '65%'}
                height="48px"
                borderRadius={align === 'right' ? '18px 18px 4px 18px' : '4px 18px 18px 18px'}
            />
        </div>
    );
}

function SkeletonField() {
    return (
        <div className="skeleton-field">
            <Skeleton width="30%" height="12px" />
            <Skeleton width="100%" height="40px" borderRadius="10px" />
        </div>
    );
}

export function ProfileSkeleton({ fields = 4 }: { fields?: number } = {}) {
    return (
        <section
            className="profile-skeleton"
            role="status"
            aria-busy="true"
            aria-label="Loading profile"
        >
            <div className="skeleton-card-header">
                <Skeleton width="80px" height="80px" shape="circle" />
                <div className="skeleton-card-info">
                    <Skeleton width="50%" height="20px" />
                    <Skeleton width="30%" height="14px" />
                </div>
            </div>
            {Array.from({ length: fields }, (_, i) => (
                <SkeletonField key={i} />
            ))}
        </section>
    );
}
